/**
 * Browser-side pretvorba slike v WebP s pomočjo canvas API-ja.
 * Vrne nov Blob ali null, če pretvorba ni mogoča (npr. SSR, SVG, ali brez podpore).
 */
export async function toWebp(
  file: File,
  { maxDim = 1920, quality = 0.85 }: { maxDim?: number; quality?: number } = {},
): Promise<Blob | null> {
  if (typeof window === "undefined") return null;
  if (!file.type.startsWith("image/")) return null;
  // SVG-jev ne rastriziramo — ostanejo kot so.
  if (file.type === "image/svg+xml") return null;

  const bitmap = await loadBitmap(file);
  if (!bitmap) return null;

  const { width, height } = bitmap;
  const scale = Math.min(1, maxDim / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, w, h);
  if ("close" in bitmap && typeof (bitmap as ImageBitmap).close === "function") {
    (bitmap as ImageBitmap).close();
  }

  return await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/webp", quality);
  });
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement | null> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // fall through
    }
  }
  return await new Promise<HTMLImageElement | null>((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}
